import { useEffect, useState } from "react";

import type { LessonLock } from "@canvaschool/contracts";

export type CoverMode = "NONE" | "SCREEN";

/**
 * The teacher is never covered, and a student released early sees the canvas
 * while the rest of the class still waits.
 */
export const coverModeFor = (
  lock: LessonLock,
  studentNumber: number | null,
): CoverMode => {
  if (lock.mode === "OFF" || studentNumber === null) {
    return "NONE";
  }
  if (lock.exemptStudentNumbers.includes(studentNumber)) {
    return "NONE";
  }
  return lock.mode;
};

const formatRemaining = (milliseconds: number): string => {
  const total = Math.max(0, Math.ceil(milliseconds / 1_000));
  const minutes = Math.floor(total / 60);
  return `${minutes}:${String(total % 60).padStart(2, "0")}`;
};

export type CoverOverlayProps = {
  lock: LessonLock;
  /** Null for a teacher. */
  studentNumber: number | null;
  now?: () => number;
};

/**
 * Swallows every pointer event that reaches it, so nothing the child touches
 * gets through to the canvas.
 */
export const CoverOverlay = ({
  lock,
  studentNumber,
  now = Date.now,
}: CoverOverlayProps) => {
  const mode = coverModeFor(lock, studentNumber);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (mode === "NONE" || lock.endsAt === null) {
      return;
    }
    const handle = window.setInterval(() => setTick((value) => value + 1), 500);
    return () => window.clearInterval(handle);
  }, [mode, lock.endsAt]);

  if (mode === "NONE") {
    return null;
  }

  const remaining =
    lock.endsAt === null
      ? null
      : formatRemaining(Date.parse(lock.endsAt) - now());
  void tick;

  return (
    <div
      className="canvaschool-cover canvaschool-cover--screen"
      role="status"
      aria-live="polite"
      aria-label="선생님이 화면을 멈췄습니다"
      onPointerDown={(event) => event.preventDefault()}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="canvaschool-cover-panel">
        <p className="canvaschool-cover-message">
          {lock.message.trim() === "" ? "선생님을 보세요" : lock.message}
        </p>
        {remaining !== null && (
          <p className="canvaschool-cover-timer">{remaining}</p>
        )}
      </div>
    </div>
  );
};
